import {
  IDataState,
  DataModules,
  DEFAULT_LIST_SCOPE
} from "@/store/modules/data/index";
import { ActionTree, GetterTree } from "vuex";
import { IState } from "@/store";
import { Methods } from "@/models/methods";
import _ from "@/boot/lodash";
import { ApiPathGetter } from "@/models/api";

const initialState = {} as IDataState;

const getters: GetterTree<IDataState, IState> = {
  apiPath: (): ApiPathGetter => () => {
    const admin = `api/admin/pricelists`;
    return { admin };
  },
  scope: () => id => {
    return `$priceList_${id}`;
  },
  params: (state: IDataState, getters) => (brand_id = 0) => {
    return _.get(
      state,
      `${
        brand_id ? getters.priceListsScope(brand_id) : DEFAULT_LIST_SCOPE
      }.params`,
      {}
    );
  },
  priceListsScope: () => (brand_id = 0) => {
    return brand_id ? `$priceLists_${brand_id}` : DEFAULT_LIST_SCOPE;
  }
};

const actions: ActionTree<IDataState, IState> = {
  list: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/list",
      {
        ...payload,
        path: getters.apiPath().admin,
        storeModule: DataModules.PRICELISTS
      },
      { root: true }
    );
  },
  get: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/get",
      {
        ...payload,
        path: `${getters.apiPath().admin}/${payload?.id}`,
        storeModule: DataModules.PRICELISTS
      },
      { root: true }
    );
  },
  create: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/create",
      {
        ...payload,
        path: `${getters.apiPath().admin}`,
        storeModule: DataModules.PRICELISTS
      },
      { root: true }
    );
  },
  update: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/update",
      {
        data: payload.data,
        path: `${getters.apiPath().admin}/${payload?.id}`,
        storeModule: DataModules.PRICELISTS
      },
      { root: true }
    );
  },

  remove: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/remove",
      {
        path: `${getters.apiPath().admin}/${payload?.id}`,
        storeModule: DataModules.CATALOGUE_PROMOTIONS
      },
      { root: true }
    );
  },

  updateProductPrices: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/callApi",
      {
        method: Methods.PUT,
        path: `${getters.apiPath().admin}/${payload?.pricelistId}/${
          payload?.id
        }`,
        storeModule: DataModules.PRICELISTS,
        requestConfig: { data: payload.data }
      },
      { root: true }
    );
  },

  assignProductPrices: ({ dispatch, getters }, payload) => {
    if (!_.has(payload, "id")) {
      throw new Error("Product id not provided");
    }

    if (!_.has(payload, "pricelistId")) {
      throw new Error("Pricelist id not provided");
    }

    if (!_.has(payload, "data.prices")) {
      throw new Error("Product prices not provided");
    }

    return dispatch(
      "data/callApi",
      {
        method: Methods.POST,
        path: `${getters.apiPath().admin}/${payload?.pricelistId}/${
          payload?.id
        }`,
        storeModule: DataModules.PRICELISTS,
        requestConfig: { data: payload.data }
      },
      { root: true }
    );
  },

  deleteProductPrices: ({ dispatch, getters }, payload) => {
    if (!_.has(payload, "id")) {
      throw new Error("Product id not provided");
    }

    if (!_.has(payload, "pricelistId")) {
      throw new Error("Pricelist id not provided");
    }

    if (!_.has(payload, "data.ids")) {
      throw new Error("Product prices not provided");
    }

    return dispatch(
      "data/callApi",
      {
        method: Methods.DELETE,
        path: `${getters.apiPath().admin}/${payload?.pricelistId}/${
          payload?.id
        }`,
        storeModule: DataModules.PRICELISTS,
        requestConfig: { data: payload.data }
      },
      { root: true }
    );
  },

  openAddEditPriceListModal: ({ dispatch }, payload) => {
    return dispatch(
      "ui/open/slideModal",
      {
        config: {
          component: () =>
            import(
              "@/components/app/admin/catalogue/priceLists/addEditPriceListModal.vue"
            ),
          width: 720,
          ...payload
        }
      },
      { root: true }
    );
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions
};
